<template>
  <div class="import-blood-report">
    <Dialog width="895px" :canCloseManually="false" :showCloseIcon="true">
      <template slot="title">
        <h1 class="title">
          {{ isVisitReadOnly ? 'Imported Blood Reports' : modalImportBloodReport.title }}
        </h1>
      </template>
      <template slot="body">
        <div class="container">
          <template v-if="step === 1">
            <div class="import-view">
              <div v-if="!isVisitReadOnly" class="import-section">
                <div class="import-card">
                  <h3 class="import-card__title">Add from documents</h3>
                  <div class="import-card__select select-documents">
                    <Select
                      outlined
                      radius="8"
                      placeholder="Select file"
                      :items="availableDocuments"
                      @change="selectedDocumentId = $event"
                      attach=".import-view"
                      :menu-props="{ contentClass: 'wide-import-menu' }"
                      is-slotable-item
                    >
                      <template slot="item" slot-scope="{ item }">
                        <div class="dropdown-item-layout">
                          <span class="dropdown-item-name text-truncate">{{ item.text }}</span>
                          <span class="dropdown-item-date">
                            {{ $moment(item.date).format('MM/DD/YYYY, hh:mm A') }}
                          </span>
                        </div>
                      </template>
                    </Select>
                    <Button
                      height="36"
                      width="65"
                      btn-bg="dark-green"
                      dark
                      :disabled="!selectedDocumentId"
                      :styles="{ 'border-radius': '8px', 'font-size': '13px' }"
                      @click="handleAddClick"
                    >
                      Add
                    </Button>
                  </div>
                </div>
                <div class="import-separator">or</div>
                <div
                  class="dropzone"
                  :class="{ 'is-dragover': isDragover }"
                  @dragover.prevent="isDragover = true"
                  @dragleave.prevent="isDragover = false"
                  @drop.prevent="onFileDrop"
                  @click="$refs.fileInput.click()"
                >
                  <input
                    type="file"
                    ref="fileInput"
                    class="hidden-input"
                    accept=".pdf,.png,.jpg,.jpeg"
                    @change="onFileSelect"
                  >
                  <div class="dropzone-content">
                    <UploadCloudIcon />
                    <div>
                      <p class="dropzone-title">drop New file here or click to upload</p>
                      <p class="dropzone-hint">PDF, PNG or JPG (max. 2MB)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="file-table-header">
                <div class="col-name">File name</div>
                <div class="col-type">Type</div>
                <div class="col-date">Upload date</div>
                <div class="col-markers">Markers</div>
                <div class="col-status">Status</div>
                <div class="col-action"></div>
              </div>

              <div class="file-list-container">
                <div v-if="!importSessions.length" class="no-data-text">
                  No Imported Data
                </div>
                <div v-else class="files-list">
                  <div
                    v-for="(session, idx) in importSessions"
                    :key="session.id"
                    class="file-item"
                    :class="{ 'is-active': selectedFileIndex === idx }"
                    @click="selectSession(session, idx)"
                  >
                    <div class="file-item__col col-name">
                      <FileIcon />
                      <span>{{ session.fileName }}</span>
                    </div>
                    <div class="file-item__col col-type">
                      <span>{{ session.importSourceDataName }} Upload</span>
                    </div>
                    <div class="file-item__col col-date">{{ $moment(session.createdOn).format('MM/DD/YYYY, hh:mm A') }}</div>
                    <div class="file-item__col col-markers">{{ session.valueCount }}</div>

                    <div class="file-item__col col-status">
                      <SuccessIcon v-if="session.status === 'InProgress' || session.status === 'Approved'"/>
                      <template v-else-if="session.status === 'Rejected' || session.status === 'Error'">
                        <v-tooltip top :max-width="400">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-on="on" v-bind="attrs" style="width: 20px; height: 20px; display: flex">
                              <ErrorIcon class="icon-error" />
                            </span>
                          </template>
                          <span>{{ session.errorMessage || 'Something went wrong' }}</span>
                        </v-tooltip>
                      </template>
                    </div>
                    <div class="file-item__col col-actions">
                      <DeleteIcon v-if="!isVisitReadOnly" @click.stop="confirmRemove(session)" style="cursor: pointer"/>
                    </div>
                  </div>
                </div>
              </div>

              <div class="session-detail-preview">
                <BloodImportReportPreview
                  v-if="importSessions.length && localSession"
                  :reportInfo="reportInfo"
                  :patient="patient"
                  :markersData="localMarkers"
                  :headers="headers"
                  :expandedRows="expandedRows"
                  :showActions="true"
                  @toggle-group="toggleRow"
                  @download="downloadSingle"
                  @print="printAction"
                  @email="emailAction"
                />

                <div v-else class="empty-placeholder-card">
                  <div class="placeholder-table-header">
                    <div v-for="header in headers" :key="header.text" :style="{ width: header.width }">
                      {{ header.text }}
                    </div>
                  </div>
                  <div class="placeholder-content">
                    <span>Select import data to see blood result</span>
                  </div>
                </div>
              </div>

            </div>
          </template>

          <div v-else class="new-upload">
            <h2 class="new-upload__title">New Upload</h2>

            <div class="file-table-header">
              <div class="col-name">File name</div>
              <div class="col-type">Type</div>
              <div class="col-date">Upload date</div>
              <div class="col-markers">Markers</div>
            </div>
            <div class="upload-bar">
              <div class="upload-bar__col bar-col-name">
                <FileIcon />
                <span>{{ reportInfo.fileName }}</span>
              </div>
              <div class="upload-bar__col bar-col-type">
                <span>{{ reportInfo.source }} Upload</span>
              </div>
              <div class="upload-bar__col bar-col-date">{{ reportInfo.createdOn }}</div>
              <div class="upload-bar__col bar-col-markers">{{ reportInfo.valueCount }}</div>
            </div>
            <BloodImportReportPreview
              :patient="patient"
              :reportInfo="reportInfo"
              :markersData="localMarkers"
              :headers="headers"
              :expandedRows="expandedRows"
              @toggle-group="toggleRow"
            />
          </div>

          <div v-if="loading" class="loading d-flex justify-center px-2 py-2 align-center">
            <v-progress-circular
              indeterminate
              color="#008C89"
            ></v-progress-circular>
          </div>
        </div>
      </template>
      <template slot="actions" v-if="step === 2">
        <div class="d-flex justify-end align-center pt-5" style="width: 100%">
          <Button height="56"
                  width="100"
                  btn-bg="white"
                  :styles="{ 'color': '#16192C', 'margin-right': '20px', 'border': '1px solid #EDF2F7' }"
                  @click="handleCancelStepTwo"
          >Cancel</Button>
          <Button height="56"
                  width="100"
                  btn-bg="dark-green"
                  dark
                  @click="confirmApprove"
          >Confirm</Button>
        </div>
      </template>
    </Dialog>
    <confirm v-if="dialogs.confirm.isOpen" />

    <div class="custom-alert">
      <v-alert :color="!isErrorMessage ? '#7ac1fb' : 'rgb(207, 70, 85)'" dismissible type="info" v-model="alertIsOpen">
        <span style="font-size: 14px">{{ popupMessage }}</span>
      </v-alert>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import Dialog from '@/components/layouts/dialog'
import BloodImportReportPreview from '@/components/layouts/blood/BloodImportReportPreview.vue'
import Button from '@/components/elements/Button.vue'
import Img from '@/components/elements/Img'
import Select from '@/components/elements/Select'
import confirm from '@/components/layouts/dialogs/confirm-dialog'
import UploadCloudIcon from '@/assets/icons/components/upload-cloud.svg'
import FileIcon from '@/assets/icons/components/file.svg'
import DeleteIcon from '@/assets/icons/components/delete-icon.svg'
import SuccessIcon from '@/assets/icons/components/success-icon-green.svg'
import ErrorIcon from '@/assets/icons/components/error-icon-red.svg'
import Image from '@/mixins/Image'
import { BLOOD_MARKERS_HEADERS } from '@/constants/tableHeaders'

export default {
  name: 'modal-import-blood-report',
  mixins: [ Image ],
  components: {
    Dialog,
    BloodImportReportPreview,
    Button,
    Img,
    Select,
    confirm,
    UploadCloudIcon,
    FileIcon,
    DeleteIcon,
    SuccessIcon,
    ErrorIcon
  },
  data: () => ({
    step: 1,
    loading: false,
    isErrorMessage: false,
    alertIsOpen: false,
    popupMessage: '',
    localSession: [],
    localMarkers: [],
    isDragover: false,
    selectedFileIndex: null,
    expandedRows: [],
    previewUrl: null,
    files: [],
    selectedDocumentId: null,
    headers: BLOOD_MARKERS_HEADERS
  }),
  computed: {
    ...mapState('blood', {
      importSessions: state => state.importSessions,
      availableDocumentsFromStore: state => state.availableDocuments
    }),
    ...mapState('dialogs', {
      modalImportBloodReport: state => state.modalImportBloodReport
    }),
    ...mapState({
      visit: state => state.visits?.visit,
    }),
    dialogs() {
      return this.$store.state.dialogs
    },
    patient() {
      return this.modalImportBloodReport.data?.patient || {}
    },
    reportInfo() {
      const s = this.localSession
      if (!s) return {}

      const nameParts = [s.patientFirstName, s.patientLastName].filter(Boolean)
      const fullName = nameParts.length > 0 ? nameParts.join(' ') : '-'

      return {
        id: s.documentId || s.id,
        fileName: s.fileName,
        source: s.ImportSourceDataName || 'Practitioner',
        patientName: fullName,
        patientDob: s.patientDateOfBirth ? this.$moment(s.patientDateOfBirth).format('MM/DD/YYYY') : '-',
        dateCollected: s.dateCollected ? this.$moment(s.dateCollected).format('MM/DD/YYYY') : '-',
        dateReceived: s.dateReceived ? this.$moment(s.dateReceived).format('MM/DD/YYYY') : '-',
        createdOn: s.createdOn ? this.$moment(s.createdOn).format('MM/DD/YYYY, hh:mm A') : '-',
        labName: s.labName || 'Unknown Lab',
        fasting: s.isFasting ? 'Yes' : 'No',
        valueCount: s.valueCount || 0
      }
    },

    availableDocuments() {
      const items = []
      if (!this.availableDocumentsFromStore || !this.availableDocumentsFromStore.length) {
        return []
      }

      this.availableDocumentsFromStore.forEach(group => {
        if (group.sourceDataTypeName) {
          items.push({ header: group.sourceDataTypeName })
        }

        const docs = group.documents?.$values || []

        docs.forEach(doc => {
          items.push({
            text: doc.fileName,
            value: doc.id,
            date: doc.createdOn
          })
        })
      })

      return items
    },

    isVisitReadOnly() {
      if (!this.visit) return false

      return (
        this.visit.finishedOn !== null ||
        ['InProtocol', 'NeedProduct', 'Completed', 'ProtocolEnding'].includes(this.visit.status)
      )
    },
  },
  watch: {
    selectedFileIndex(newIdx) {
      this.generatePreview(newIdx)
    },
    alertIsOpen(val) {
      const self = this
      if (val) {
        setTimeout(function () {
          self.alertIsOpen = false
        }, 5000)
      }
    }
  },
  created() {
    this.initData()
  },
  beforeDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
    }
  },
  methods: {
    ...mapActions('blood', [
      'uploadBloodReport',
      'fetchImportSessions',
      'fetchSessionDetail',
      'fetchAvailableDocuments',
      'processDocument'
    ]),
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),
    ...mapActions('documents', [
      'downloadDocumentFile',
      'sendDocumentsToPatient'
    ]),

    async initData(manageLoading = true) {
      if (manageLoading) this.loading = true
      const visitId = this.modalImportBloodReport.data?.visitId

      await this.fetchAvailableDocuments(visitId)
      const res = await this.fetchImportSessions(visitId)

      const sessionsList = res?.sessions?.$values || []

      if (!res?.success || sessionsList.length === 0) {
        this.selectedFileIndex = null
        if (manageLoading) this.loading = false
        return
      }

      const lastIdx = this.importSessions.length - 1
      const lastSession = this.importSessions[lastIdx]

      await this.selectSession(lastSession, lastIdx)

      if (manageLoading) this.loading = false
    },

    close() {
      this.TOGGLE_DIALOG({name: 'modalImportBloodReport', isOpen: false, root: true})
    },

    async selectSession(session, idx) {
      if (this.selectedFileIndex === idx)  return

      this.selectedFileIndex = idx
      this.loading = true

      const res = await this.fetchSessionDetail(session.id)

      if (!res) {
        this.loading = false
        return
      }

      this.localSession = res.session
      this.localMarkers = res.groups
      this.expandedRows = res.groups.map(g => g.name)

      this.loading = false
    },

    async confirmApprove() {
      await this.TOGGLE_DIALOG({
        id: this.localSession.id,
        name: 'confirm',
        isOpen: true,
        action: 'blood/approveBloodReport',
        callback: (async () => {
          this.TOGGLE_DIALOG({ name: 'confirm', isOpen: false })
          this.loading = true
          try {
            this.step = 1
            await this.initData(false)

            const onSuccess = this.modalImportBloodReport.data?.onSuccess
            if (typeof onSuccess === 'function') {
              await onSuccess()
            }
          } catch (e) {
            console.error('Refresh error', e)
          } finally {
            this.close()
            this.loading = false
          }
        }),
        title: 'Confirm Approval',
        description: 'Are you sure you want to approve this blood report?'
      })
    },

    async handleCancelStepTwo() {
      await this.TOGGLE_DIALOG({
        id: this.localSession.id,
        name: 'confirm',
        isOpen: true,
        action: 'blood/rejectBloodReport',
        callback: (async () => {
          this.step = 1
          await this.initData()
        }),
        title: 'Confirm Cancellation',
        description: 'Are you sure you want to cancel? This upload will be rejected and removed from processing.'
      })
    },

    async confirmRemove(session) {
      await this.TOGGLE_DIALOG({
        id: session.id,
        name: 'confirm',
        isOpen: true,
        action: 'blood/removeBloodReport',
        callback: (async () =>  {
          await this.initData()
        }),
        title: 'Confirm removing the report',
        description: `Are you sure you want to remove: ${session.fileName}?`
      })
    },

    async prepareFileForSummary(file) {
      this.loading = true

      const res = await this.uploadBloodReport({ file, visitId: this.modalImportBloodReport.data?.visitId })

      if (!res) {
        this.loading = false
        return
      }

      this.localSession = res.session
      this.localMarkers = res.groups

      this.expandedRows = res.groups.map(g => g.name)

      this.step = 2
      this.loading = false
    },

    toggleRow(name) {
      const index = this.expandedRows.indexOf(name)
      if (index > -1) {
        this.expandedRows.splice(index, 1)
      } else {
        this.expandedRows.push(name)
      }
    },

    onFileSelect(e) {
      const file = e.target.files[0]
      if (file) this.prepareFileForSummary(file)
    },

    onFileDrop(e) {
      this.isDragover = false
      const file = e.dataTransfer.files[0]
      if (file) this.prepareFileForSummary(file)
    },

    generatePreview(idx) {
      this.cleanupPreviewUrl()

      if (idx !== null && this.files[idx]) {
        this.previewUrl = URL.createObjectURL(this.files[idx]) + '#toolbar=0&navpanes=0&scrollbar=0'
      }
    },

    cleanupPreviewUrl() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = null
      }
    },

    async handleAddClick() {
      if (!this.selectedDocumentId) return

      this.loading = true
      const visitId = this.modalImportBloodReport.data?.visitId

      const res = await this.processDocument({
        visitId,
        documentId: this.selectedDocumentId
      })

      this.localSession = res.session
      this.localMarkers = res.groups

      if (res.groups.length) {
        this.expandedRows = res.groups.map(g => g.name)
      }

      this.step = 2
      this.loading = false
    },

    async downloadSingle(report) {
      this.loading = true
      try {
        let blobData = null
        let fileName = report ? report.fileName : 'document.pdf'

        blobData = await this.downloadDocumentFile({ id: report.id })

        if (blobData) {
          const url = window.URL.createObjectURL(new Blob([blobData]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        } else {
          this.isErrorMessage = true
          this.alertIsOpen = true
          this.popupMessage = 'Failed to prepare download. Please try again.'
        }
      } catch (e) {
        console.error('Error loading source file:', e)
        this.isErrorMessage = true
        this.alertIsOpen = true
        this.popupMessage = await this.getErrorMessage(e)
      } finally {
        this.loading = false
      }
    },

    async printAction(documentId) {
      this.loading = true

      try {
        const blobData = await this.downloadDocumentFile({ id: documentId })

        if (!blobData) {
          this.isErrorMessage = true
          this.alertIsOpen = true
          this.popupMessage = 'Document not found.'
          return
        }

        const oldIframe = document.getElementById('report-print-iframe')
        if (oldIframe) {
          document.body.removeChild(oldIframe)
        }

        const url = window.URL.createObjectURL(new Blob([blobData], { type: 'application/pdf' }))
        const iframe = document.createElement('iframe')
        iframe.id = 'report-print-iframe'
        iframe.src = url

        Object.assign(iframe.style, {
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          width: '100%',
          height: '100%',
          border: '0',
          opacity: '0',
        })

        document.body.appendChild(iframe)

        iframe.onload = () => {
          iframe.contentWindow?.focus()
          setTimeout(() => {
            iframe.contentWindow?.print()
          }, 200)

          const cleanUp = () => {
            if (iframe.parentNode) {
              document.body.removeChild(iframe)
            }
            window.URL.revokeObjectURL(url)
            window.removeEventListener('focus', cleanUp)
          }

          iframe.contentWindow?.addEventListener('afterprint', cleanUp)
          window.addEventListener('focus', cleanUp)

          setTimeout(() => {
            cleanUp()
          }, 60000)
        }
      } catch (e) {
        console.error('Error printing document:', e)
        this.isErrorMessage = true
        this.alertIsOpen = true
        this.popupMessage = await this.getErrorMessage(e)
      } finally {
        this.loading = false
      }
    },

    async emailAction(report) {
      const email = this.patient.email
      const patientId = this.$route.params.id

      await this.TOGGLE_DIALOG({
        id: email,
        name: 'confirm',
        isOpen: true,
        title: 'Confirm Sending Email',
        description: `Send ${report.fileName} document to ${email}?`,
        callback: async () => {
          this.TOGGLE_DIALOG({ name: 'confirm', isOpen: false })
          this.loading = true

          try {
            const res = await this.sendDocumentsToPatient({ patientId, documentIds: report.id })
            if (res?.success) {
              this.isErrorMessage = false
              this.popupMessage = 'Documents sent successfully!'
            } else {
              this.isErrorMessage = true
              this.popupMessage = 'Failed to send email'
            }
            this.alertIsOpen = true
          } catch (e) {
            this.isErrorMessage = true
            this.alertIsOpen = true
            this.popupMessage = 'A server error occurred'
          } finally {
            this.loading = false
          }
        }
      })
    },
    async getErrorMessage(error) {
      let message = 'An error occurred'

      if (error?.response?.data) {
        const data = error.response.data

        if (data instanceof Blob) {
          let text = ''
          try {
            text = await data.text()
            const json = JSON.parse(text)
            message = json.message || json.error || text
          } catch (_) {
            if (text) {
              message = text
            }
          }
        } else if (typeof data === 'string') {
          message = data
        } else if (data && typeof data === 'object') {
          message = data.message || data.error || message
        }
      } else if (error?.message) {
        message = error.message
      }

      return message
    }
  }
}
</script>

<style lang="scss" scoped>
$file-grid: 300px 165px 180px 70px 1fr 44px;
$summary-grid: 407px 165px 180px 1fr;

%table-col {
  color: #1A202C;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 12px;
}

@mixin flex-row($gap: 8px) {
  display: flex;
  align-items: center;
  column-gap: $gap;
}

.import-blood-report {
  ::v-deep .v-dialog {
    //height: 95vh;
    max-height: 880px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: 1496px) {
      max-height: 100vh;
    }

    .v-card {
      max-height: 880px;
      display: flex;
      flex-direction: column;
      //position: relative;
      overflow: hidden;

      @media (max-width: 1496px) {
        //height: 100vh;
        max-height: 100vh;
      }

      &__title {
        flex: 0 0 auto;
        padding: 36px 36px 25px !important;

        .title {
          color: #27272E;
          font-size: 28px !important;
          font-weight: 600;
          letter-spacing: 0 !important;
        }

        .image {
          cursor: pointer;
        }
      }

      &__text {
        display: flex;
        flex-direction: column;

        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
      }

      &__actions {
        flex: 0 0 auto;
        padding: 20px 36px 30px;

        .v-btn {
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0;
          border-radius: 16px !important;
        }
      }

      .container {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        padding: 0 36px;
        overflow: hidden;
      }

      .import-section {
        display: flex;
        align-items: center;
        justify-content: space-between;
        column-gap: 23px;

        .import-card {
          flex: 1 1 0;
          min-width: 0;
          border: 1px dashed #CBD5E0;
          background-color: #F7FAFC;
          border-radius: 8px;
          padding: 16px 16px 12px 13px;

          &__title {
            color: #27272E;
            font-size: 16px;
            font-weight: 600;
            line-height: 25px;
            letter-spacing: 0;
            padding-bottom: 9px;
          }

          &__select {
            display: flex;
            justify-content: space-between;
            column-gap: 9px;

            .select {
              height: 36px;
              flex: 1 1 0;
              min-width: 0;

              .v-input {
                height: 100%;
                box-shadow: none;
                background-color: #EDF2F7;
                border: 2px solid transparent;
                transition: border-color, background-color 0.3s ease;

                &.v-select--is-menu-active {
                  border-color: #008C89;
                  background-color: #FFFFFF;

                  .v-input__append-inner {

                    .v-input__icon .v-icon {
                      background-color: rgba(0, 140, 137, 0.1) !important;
                      color: #008C89 !important;
                    }
                  }
                }

                .v-input__control {
                  .v-input__slot {
                    padding: 0 5px 0 16px;

                    .v-select__slot {
                      .v-select__selections {
                        padding: 0;

                        input::placeholder {
                          color: #2D3748;
                          font-size: 16px;
                          font-weight: 400;
                        }
                      }
                      .v-input__append-inner {
                        margin-top: 5px !important;

                        .v-input__icon .v-icon {
                          background: none;
                          border-radius: 50%;
                          color: #B5B5BD;

                          &.primary--text {
                            color: #B5B5BD !important;
                            caret-color: #B5B5BD !important;
                          }
                        }
                      }
                    }
                  }
                  .v-text-field__details {
                    display: none !important;
                  }
                }
              }
            }

            .button {
              .v-btn.v-btn--disabled {
                background-color: #008C89 !important;
                cursor: not-allowed !important;
              }
            }
          }
        }

        .import-separator {
          color: #1A202C;
          font-size: 16px;
          font-weight: 400;
          letter-spacing: 0;
        }

        .dropzone {
          flex: 1 1 0;
          min-width: 0;
          height: 100px;
          display: flex;
          border: 1px dashed #9FB6FF;
          background-color: rgba(208, 220, 255, 0.3);
          border-radius: 8px;
          padding: 12px 16px;
          cursor: pointer;
          transition: all 0.3s ease;

          .dropzone-content {
            display: flex;
            align-items: center;
            column-gap: 10px;
            font-size: 16px;
            font-weight: 400;

            svg {
              flex-shrink: 0;
              width: 48px;
              height: 48px;
            }
          }

          &-title {
            color: #1A202C;
            margin-bottom: 4px;
          }

          &-hint {
            color: #718096;
            margin-bottom: 0;
          }
        }
      }

      .file-table-header {
        height: 36px;
        display: grid;
        grid-template-columns: $file-grid;
        align-items: center;
        flex: 0 0 auto;
        margin-top: 18px;

        div {
          color: #8492A6;
          font-size: 10px;
          font-weight: 600;
          line-height: 16px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          padding-left: 14px;

          &.col-type {
            padding-left: 24px;
          }
        }
      }

      .file-list-container {
        min-height: 82px;
        display: flex;
        align-items: center;
        justify-content: center;

        .no-data-text {
          color: #A0AEC0;
          font-size: 18px;
          font-weight: 400;
          line-height: 30px;
        }

        .files-list {
          width: 100%;

          .file-item {
            display: grid;
            grid-template-columns: $file-grid;
            align-items: center;
            border-radius: 8px 8px 0 0;
            padding: 14px 0;
            margin-bottom: 4px;
            cursor: pointer;
            transition: background-color 0.3s ease;

            &.is-active {
              background-color: #EBF2FA;
            }

            &__col {
              @extend %table-col;
            }

            .col-status {
              padding-left: 12px;
            }

            .col-type {
              padding-left: 20px;
            }

            .col-date {
              color: #2D3748;
            }

            .col-name {
              @include flex-row(10px);

              svg {
                flex-shrink: 0;
              }

              span {
                position: relative;

                &::after {
                  content: "";
                  position: absolute;
                  bottom: 3px;
                  left: 0;
                  width: 100%;
                  height: 1px;
                  background-color: #1A202C;
                }
              }
            }

            .col-actions {
              @include flex-row(26px);

              svg {
                width: 20px;
                height: 20px;
              }
            }
          }
        }
      }

      .session-detail-preview {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;

        .empty-placeholder-card {
          width: 100%;
          min-height: 350px;
          background-color: #EBF2FA;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          padding: 96px 10px 15px;
          overflow: hidden;

          .placeholder-table-header {
            display: flex;
            align-items: center;
            height: 32px;
            background-color: #FFFFFF;
            border-bottom: 1px solid #EDF2F7;

            div {
              color: #8492A6;
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
              text-align: center;
              letter-spacing: 0.6px;
            }
          }

          .placeholder-content {
            flex-grow: 1;
            background-color: #FAFAFA;
            display: flex;
            align-items: center;
            justify-content: center;

            span {
              color: #A0AEC0;
              font-size: 18px;
              font-weight: 400;
              line-height: 30px;
            }
          }
        }
      }

      .new-upload {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;

        padding-top: 11px;

        .file-table-header {
          grid-template-columns: $summary-grid;
          margin-top: 14px;
        }

        &__title {
          color: #27272E;
          font-size: 18px;
          font-weight: 600;
          line-height: 27px;
          letter-spacing: -0.1px;
        }

        .upload-bar {
          flex: 0 0 auto;
          height: 52px;
          display: grid;
          grid-template-columns: $summary-grid;
          align-items: center;
          background-color: #EBF2FA;
          border-radius: 8px 8px 0 0;
          margin-bottom: 4px;

          &__col {
            @extend %table-col;
          }

          .bar-col-type {
            padding-left: 20px;
          }

          .bar-col-date {
            color: #2D3748;
          }

          .bar-col-name {
            @include flex-row(10px);
            font-weight: 500;

            svg {
              flex-shrink: 0;
            }

            span {
              position: relative;

              &::after {
                content: "";
                position: absolute;
                bottom: 3px;
                left: 0;
                width: 100%;
                height: 1px;
                background-color: #1A202C;
              }
            }
          }

          .bar-col-markers {
            text-align: center;
            padding: 0;
          }
        }
      }

      .table-wrapper {
        .table {
          .v-data-table {
            border-radius: 0;
          }
        }
      }
    }
  }

  .confirm-dialog {
    ::v-deep .v-dialog {
      height: auto;
      overflow-y: auto !important;

      .v-card {
        height: auto !important;
        display: block;
      }
    }
  }

  .import-view {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;

    ::v-deep .wide-import-menu {
      top: 97px !important;
      left: 0px !important;
      right: 0px !important;
      width: 100% !important;
      max-width: none !important;
      min-width: 100% !important;
      border-radius: 10px;
      overflow: hidden !important;

      .v-list {
        padding: 0;
        max-height: 312px;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        box-shadow: 0px 20px 80px -40px #2D325A1A,
        0px -3px 0px 0px #4254664D;

        &::-webkit-scrollbar {
          width: 26px;
        }

        &::-webkit-scrollbar-track {
          background-color: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background-color: #A6B7D4;
          border-radius: 16px;
          border: 10px solid transparent;
          background-clip: padding-box;
        }

        .v-subheader {
          height: 52px !important;
          min-height: 52px !important;
          color: #1A202C;
          font-size: 15px;
          font-weight: 600;
          line-height: 20px;
          border-bottom: 1px solid #E4ECF7;
          padding: 0 20px 0;
        }

        .v-list-item {
          height: 52px !important;
          min-height: 52px !important;
          border-bottom: 1px solid #E4ECF7;
          padding: 0 46px 0 20px;

          &:last-child {
            border-bottom: none;
          }
        }
      }

      .dropdown-item-layout {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;
        font-size: 15px;
        font-weight: 400;

        .dropdown-item-name {
          flex: 1;
          min-width: 0;
          color: #1A202C;
        }

        .dropdown-item-date {
          color: #2D3748;
          white-space: nowrap;
          padding: 0 20px 0 12px;
        }
      }
    }
  }

  .loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgb(213 213 213 / 50%);
    z-index: 999;
  }

  .custom-alert {
    max-width: 300px;
    position: fixed;
    top: 10px;
    right: 20px;
    z-index: 999;
  }
}

.hidden-input { display: none; }
</style>
